
import Vue from 'vue';
import { Route } from 'vue-router';
import { mapActions, mapState, mapGetters } from 'vuex';

import '@/icons';
import config from '@/config';
import { formatNumber } from '@/i18n';

import { RESOURCES_LIST_PER_PAGE_OPTIONS } from '@/store/utils/filters';
import { ResourcesActions } from '@/store/modules/resources/types';
import { ReportsActions } from '@/store/modules/reports/types';
import { PublishStatus } from '@/store/types';
import createSlug from '@/utils/slug';

import NotificationBanner from '@/components/blocks/notification-banner.vue';
import ResourceBlock from '@/components/blocks/resource.vue';
import PaginationControl from '@/components/controls/pagination.vue';
import SortOrderControl from '@/components/controls/sort-order.vue';
import CheckboxControl from '@/components/controls/checkbox.vue';
import Dropdown, { DropdownItems } from '@/components/controls/dropdown.vue';
import ViewToggleControl from '@/components/controls/view-toggle.vue';
import ResourcesFilterForm from '@/components/forms/resources-filter.vue';
import ResourceBlockFeaturedSkeleton from '@/components/skeletons/resource-block-featured.vue';
import ResourcesListSkeleton from '@/components/skeletons/resources-list.vue';
import TableListSkeleton from '@/components/skeletons/table-list.vue';
import { Resource } from '@/store/modules/resources/resource';
import PersonalizeResource from '@/components/forms/personalize-resource.vue';

export default Vue.extend({
  name: 'ResourcesView',
  components: {
    PaginationControl,
    SortOrderControl,
    ViewToggleControl,
    ResourcesFilterForm,
    ResourceBlock,
    NotificationBanner,
    ResourceBlockFeaturedSkeleton,
    ResourcesListSkeleton,
    TableListSkeleton,
    Dropdown,
    CheckboxControl,
    PersonalizeResource,
  },
  data() {
    return {
      selectedItems: [],
      showPersonalizeModal: false,
      listView: 'grid' as 'grid' | 'table',
    };
  },
  computed: {
    ...mapState('user/profile', ['currentProfile']),
    ...mapState('resources', [
      'resourcesList',
      'resourcesListFilters',
      'laserHighlightedItems',
      'trainingResourcesItems',
      'featured',
      'northAmericaAsset',
      'pagination',
    ]),
    ...mapGetters('resources', [
      'listIsLoading',
      'listIsLoaded',
      'listIsEmpty',
      'featuredIsLoading',
      'featuredIsLoaded',
      'northAmericaAssetIsLoading',
      'northAmericaAssetIsLoaded',
    ]),
    ...mapGetters(['languageName']),
    ...mapGetters('user/profile', ['isSuperAdmin', 'isAdmin', 'isClinical', 'isInNorthAmerica', 'isInCA', 'isSales']),
    areAllItemsSelected(): boolean {
      return this.selectedItems.length === this.resourcesList.length;
    },
    createResourceUrl(): string {
      return config.DRUPAL_URL + 'node/add';
    },
    totalResources(): string {
      const totalItems = !this.pagination || !this.pagination.total ? 0 : this.pagination.total;

      return `${formatNumber(totalItems)} ${this.$t('Result', {
        count: totalItems,
      })}`;
    },
    canResetFilters(): boolean {
      return (
        (this.$route.query && this.$route.query.length) ||
        (this.$route.params && this.$route.params && this.$route.params.page !== '1')
      );
    },
    dropdownItems() {
      const items: DropdownItems = {};

      if (this.isSuperAdmin) {
        items.report = {
          label: this.$t('Generate Report'),
          icon: 'document',
        };
      }

      if (this.canResetFilters) {
        items.reset = {
          label: this.$t('Reset Filters'),
          icon: 'close',
        };
      }

      if (this.isSuperAdmin || this.isAdmin) {
        if (!this.areAllItemsSelected) {
          items.select_all = {
            hasTopBorder: true,
            label: this.$t('Select All'),
          };
        } else {
          items.select_none = {
            hasTopBorder: true,
            label: this.$t('Select None'),
          };
        }
      }
      if ((this.isSuperAdmin || this.isAdmin) && this.selectedItems && this.selectedItems.length) {
        items.restore = {
          label: this.$t('Restore'),
          count: this.selectedItems.length,
          disabled: !this.selectedItems.length,
        };

        items.remove = {
          label: this.$t('Delete'),
          count: this.selectedItems.length,
          disabled: !this.selectedItems.length,
        };
      }

      return items;
    },
    showFeaturedAdSlot(): boolean {
      return true;
      // return Boolean(this.$route.query) && Boolean(this.$route.query.product);
    },
    showLaserHighlightedItems(): boolean {
      return (
        this.listIsLoaded &&
        !this.listIsEmpty &&
        this.pagination &&
        this.pagination.currentPage === 1 &&
        this.laserHighlightedItems.length &&
        this.resourcesListFilters &&
        (!this.resourcesListFilters.product || !this.resourcesListFilters.product.length) &&
        (!this.resourcesListFilters.treatments || !this.resourcesListFilters.treatments.length) &&
        (!this.resourcesListFilters.categories || !this.resourcesListFilters.categories.length)
      );
    },
    showTrainingResourcesItems(): boolean {
      return (
        this.listIsLoaded &&
        !this.listIsEmpty &&
        this.pagination &&
        this.pagination.currentPage === 1 &&
        this.trainingResourcesItems.length &&
        this.resourcesListFilters &&
        this.resourcesListFilters.categories &&
        this.resourcesListFilters.categories.includes(9)
      );
    },
    canPersonalize(): boolean {
      return (
        typeof this.currentProfile !== 'undefined' &&
        Array.isArray(this.currentProfile.personalization) &&
        this.currentProfile.personalization.length > 0
      );
    },
    displayFeaturedItems() {
      // Safety check in case getters are not available yet
      if (
        typeof this.isInNorthAmerica === 'boolean' &&
        typeof this.isInCA === 'boolean' &&
        (this.isInNorthAmerica || this.isInCA)
      ) {
        const items: Resource[] = [];
        if (this.northAmericaAsset) {
          items.push(this.northAmericaAsset);
          // Add one featured item if available
          if (this.featured && this.featured.length > 0) {
            items.push(this.featured[0]);
          }
        } else {
          // If no North America asset, show 2 featured items
          if (this.featured && this.featured.length > 0) {
            items.push(...this.featured.slice(0, 2));
          }
        }
        return items;
      }
      return this.featured.slice(0, 2);
    },
    listPerPageOptions(): readonly number[] {
      return RESOURCES_LIST_PER_PAGE_OPTIONS;
    },
    listPerPage(): number {
      const raw = this.$route.query.per_page;
      if (raw === undefined || raw === null || raw === '') {
        return 15;
      }
      const v = Array.isArray(raw) ? raw[0] : raw;
      const n = Number(v);
      return RESOURCES_LIST_PER_PAGE_OPTIONS.indexOf(n) !== -1 ? n : 15;
    },
  },
  methods: {
    ...mapActions('reports', {
      requestReport: ReportsActions.REQUEST,
    }),
    ...mapActions('resources', {
      deleteResources: ResourcesActions.BULK_DELETE,
      restoreResources: ResourcesActions.BULK_RESTORE,
      loadNorthAmericaAsset: ResourcesActions.LOAD_NORTH_AMERICA_ASSET,
    }),
    handleRequestSort(sort: string) {
      this.$router.push({
        name: 'resources',
        query: {
          ...this.$route.query,
          sort,
        },
      });
    },
    handlePerPageChange(event: Event): void {
      const t = event.target as HTMLSelectElement;
      const n = Number(t.value);
      if (RESOURCES_LIST_PER_PAGE_OPTIONS.indexOf(n) === -1) {
        return;
      }
      const query: Record<string, string | string[] | undefined> = {
        ...(this.$route.query as Record<string, string | string[] | undefined>),
      };
      if (n === 15) {
        delete query.per_page;
      } else {
        query.per_page = String(n);
      }
      this.$router.push({
        name: 'resources',
        params: { ...this.$route.params, page: '1' },
        query,
      });
    },
    handleDropdownClick(action: string): void {
      if ('reset' === action) {
        this.$router.push({
          params: {
            page: '1',
          },
          query: {},
        });
      }

      if ('report' === action) {
        this.requestReport();
      }

      if ('remove' === action) {
        this.deleteResources({ ids: this.selectedItems });
      }

      if ('restore' === action) {
        this.restoreResources({ ids: this.selectedItems });
      }

      if ('select_all' === action) {
        this.selectAll();
      }

      if ('select_none' === action) {
        this.selectAll();
      }
    },
    isSelected(resource: Resource): boolean {
      return this.selectedItems.indexOf(resource.id) !== -1;
    },

    selectItem(resource: Resource) {
      if (this.isSelected(resource)) {
        this.selectedItems = this.selectedItems.filter((item) => item !== resource.id);
        return;
      }

      this.selectedItems.push(resource.id);
    },

    selectAll() {
      if (this.areAllItemsSelected) {
        this.selectedItems = [];
      } else {
        this.selectedItems = this.resourcesList.map((resource: Resource) => resource.id);
      }
    },

    closePersonalizeModal() {
      this.$router.push({ hash: undefined });
      this.showPersonalizeModal = false;
    },

    resourceDetailsLink(resource: Resource) {
      return {
        name: 'resource',
        params: { slug: createSlug(resource.title), id: resource.id },
      };
    },

    resourceIsDeleted(resource: Resource): boolean {
      return resource.status === PublishStatus.Deleted;
    },

    resourceAssetCountLabel(resource: Resource): string {
      let count = 0;
      if (resource.filesCount) {
        count += resource.filesCount;
      }
      if (resource.linksCount) {
        count += resource.linksCount;
      }
      if (resource.video && resource.video.length > 0) {
        count += 1;
      }
      return this.$t('%(count)% Asset', { count });
    },

    resourceGlobeLabel(resource: Resource): string {
      let items: string[] = [this.$t('All Countries')];
      if (resource.countries && resource.countries.length) {
        items = resource.countries.map((c) => c.name);
      }
      const lang = this.languageName(resource.languageCode);
      if (lang) {
        items.push(String(lang));
      }
      return items.join(', ');
    },

    resourceCategoriesLabel(resource: Resource): string {
      return resource.categories.map((c) => c.title).join(', ');
    },

    resourceProductsLabel(resource: Resource): string {
      if (resource.allProducts === 1) {
        return this.$t('All Products');
      }
      return resource.products.map((p) => p.title).join(', ');
    },
  },

  watch: {
    $route: {
      handler(val: Route) {
        if (val.hash === '#personalize-modal') {
          this.showPersonalizeModal = true;
        } else {
          this.showPersonalizeModal = false;
        }
      },
      immediate: true,
    },
  },
});
